import {
    type DynamicWidgetBinding,
    type DynamicWidgetIssue,
    type DynamicWidgetModel,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_schema';

/*
 * Flattened binding tree for the debug inspector. It is derived only from the
 * model's own bindings and issues — there is no second render system and no
 * template-specific knowledge here. Each binding carries explicit section,
 * row, label, and semantic-role metadata from its adapter.
 *
 * The list is flat (with a depth per node) because the inspector navigates it
 * with the arrow keys, and a flat list is one unambiguous keyboard order.
 */

export type DynamicWidgetSkeletonNodeKind =
    | 'section'
    | 'row'
    | 'field'
    | 'control'
    | 'issue';

export type DynamicWidgetSkeletonNode = {
    readonly key: string;
    readonly kind: DynamicWidgetSkeletonNodeKind;
    readonly label: string;
    readonly depth: number;
    // Null on grouping nodes (sections and rows), which have no binding of
    // their own.
    readonly bindingId: string | null;
    readonly issueCodes: ReadonlyArray<DynamicWidgetIssue['code']>;
};

function issueLabel(issue: DynamicWidgetIssue): string {
    return `${issue.code} (${issue.scope})`;
}

export function buildDynamicWidgetBindingSkeleton(
    model: DynamicWidgetModel,
): ReadonlyArray<DynamicWidgetSkeletonNode> {
    const issuesByBindingId = new Map<string, Array<DynamicWidgetIssue>>();
    const placedIssueIds = new Set<string>();
    for (const issue of model.issues) {
        for (const bindingId of issue.bindingIds) {
            const bindingIssues = issuesByBindingId.get(bindingId) ?? [];
            bindingIssues.push(issue);
            issuesByBindingId.set(bindingId, bindingIssues);
        }
    }

    const sections: Array<{
        readonly name: string;
        readonly rows: Array<{
            readonly key: string | null;
            readonly bindings: Array<DynamicWidgetBinding>;
        }>;
    }> = [];
    for (const binding of model.bindings) {
        let section = sections.find(
            (candidate) => candidate.name === binding.presentation.section,
        );
        if (section === undefined) {
            section = { name: binding.presentation.section, rows: [] };
            sections.push(section);
        }
        let row = section.rows.find(
            (candidate) => candidate.key === binding.presentation.rowKey,
        );
        if (row === undefined) {
            row = { key: binding.presentation.rowKey, bindings: [] };
            section.rows.push(row);
        }
        row.bindings.push(binding);
    }

    const nodes: Array<DynamicWidgetSkeletonNode> = [];
    for (const section of sections) {
        nodes.push({
            key: `section:${section.name}`,
            kind: 'section',
            label: section.name,
            depth: 0,
            bindingId: null,
            issueCodes: [],
        });
        for (const row of section.rows) {
            const fieldDepth = row.key === null ? 1 : 2;
            if (row.key !== null) {
                nodes.push({
                    key: `row:${row.key}`,
                    kind: 'row',
                    label: row.key,
                    depth: 1,
                    bindingId: null,
                    issueCodes: [],
                });
            }
            for (const binding of row.bindings) {
                const bindingIssues = issuesByBindingId.get(binding.id) ?? [];
                nodes.push({
                    key: `binding:${binding.id}`,
                    kind: binding.presentation.role,
                    label: binding.presentation.label,
                    depth: fieldDepth,
                    bindingId: binding.id,
                    issueCodes: bindingIssues.map((issue) => issue.code),
                });
                for (const issue of bindingIssues) {
                    // An issue can name several bindings; show it under the
                    // first one so the tree has no duplicates.
                    if (placedIssueIds.has(issue.id)) {
                        continue;
                    }
                    placedIssueIds.add(issue.id);
                    nodes.push({
                        key: `issue:${issue.id}`,
                        kind: 'issue',
                        label: issueLabel(issue),
                        depth: fieldDepth + 1,
                        bindingId: binding.id,
                        issueCodes: [issue.code],
                    });
                }
            }
        }
    }

    const unplacedIssues = model.issues.filter(
        (issue) => !placedIssueIds.has(issue.id),
    );
    if (unplacedIssues.length > 0) {
        nodes.push({
            key: 'section:issues',
            kind: 'section',
            label: 'issues',
            depth: 0,
            bindingId: null,
            issueCodes: [],
        });
        for (const issue of unplacedIssues) {
            nodes.push({
                key: `issue:${issue.id}`,
                kind: 'issue',
                label: issueLabel(issue),
                depth: 1,
                bindingId: issue.bindingIds[0] ?? null,
                issueCodes: [issue.code],
            });
        }
    }
    return nodes;
}
